import type {
  MapCategory,
  MapDocument,
  MapRoute,
  MapRouteStyle,
  MapStand,
} from "../shared/types";
import {
  inheritMapPreviewOpacity,
  isMapLocationHidden,
} from "../shared/mapPresentation";
import { mapStandHeight } from "./venueMapProjection";
import { VENUE_MAP_ROOF_TINT } from "./venueMapLighting";

export interface VenueMapStandColors {
  roof: string;
  ink: string;
}

/** One palette for the map, fallback renderer and selected-booth miniature. */
export const venueMapPalette: Record<MapCategory, VenueMapStandColors> = {
  exhibitor: { roof: "#b7e7d8", ink: "#174d40" },
  stage: { roof: "#f4bad9", ink: "#732b54" },
  lounge: { roof: "#d6c9ef", ink: "#50406b" },
  facility: { roof: "#eee0bd", ink: "#685836" },
  unassigned: { roof: "#e1e5e4", ink: "#66736f" },
};

const standColors = new WeakMap<MapStand, VenueMapStandColors>();
const standSaturations = new WeakMap<MapStand, number>();
const sourceColors = new WeakMap<
  MapStand,
  { color: string; palette: VenueMapStandColors }
>();
const validSourceColor = (stand: MapStand): string | undefined =>
  typeof stand.color === "string" && /^#[0-9a-f]{6}$/i.test(stand.color)
    ? stand.color.toLowerCase()
    : undefined;

/** Imported roofs retain their source paint; category defaults keep the existing tint. */
export function mapStandRoofTint(stand: MapStand): number {
  return validSourceColor(stand) ? 0 : VENUE_MAP_ROOF_TINT;
}

function sourceInk(color: string): string {
  const linear = [1, 3, 5].map((offset) => {
    const channel = parseInt(color.slice(offset, offset + 2), 16) / 255;
    return channel <= 0.04045
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });
  const luminance =
    linear[0] * 0.2126 + linear[1] * 0.7152 + linear[2] * 0.0722;
  // Choose the stronger black/white contrast. Unlike a simple brightness cutoff,
  // this keeps light lettering on navy roofs and readable ink on saturated fills.
  return (luminance + 0.05) / 0.05 >= 1.05 / (luminance + 0.05)
    ? "#000000"
    : "#ffffff";
}

function sourcePalette(stand: MapStand): VenueMapStandColors | undefined {
  const color = validSourceColor(stand);
  if (!color) return undefined;
  const cached = sourceColors.get(stand);
  if (cached?.color === color) return cached.palette;
  const palette = { roof: color, ink: sourceInk(color) };
  sourceColors.set(stand, { color, palette });
  return palette;
}

/** Logos follow the same temporary route emphasis as their booth roof. */
export function mapStandSaturation(stand: MapStand): number {
  return standSaturations.get(stand) ?? 1;
}

/** Source outlines follow the same temporary route softening as their booth. */
export function mapStandBorderColor(stand: MapStand): string | undefined {
  const color = stand.borderColor;
  return typeof color === "string" && /^#[0-9a-f]{6}$/i.test(color)
    ? desaturateMapColor(color.toLowerCase(), mapStandSaturation(stand))
    : undefined;
}
const appearanceSources = new WeakMap<MapDocument, MapDocument>();
const lastAppearances = new WeakMap<
  MapDocument,
  { key: string; document: MapDocument }
>();

export function mapStandColors(stand: MapStand): VenueMapStandColors {
  return (
    standColors.get(stand) ||
    sourcePalette(stand) ||
    venueMapPalette[stand.category] ||
    venueMapPalette.unassigned
  );
}

/** Stable source identity lets GPU textures survive temporary route changes. */
export function mapAppearanceSourceDocument(
  document: MapDocument,
): MapDocument {
  return appearanceSources.get(document) || document;
}

/** Preserve luminance while lowering chroma; ink stays unchanged for readability. */
export function desaturateMapColor(hex: string, saturation: number): string {
  if (saturation === 1) return hex;
  const rgb = [1, 3, 5].map((offset) =>
    parseInt(hex.slice(offset, offset + 2), 16),
  );
  const grey = rgb[0] * 0.2126 + rgb[1] * 0.7152 + rgb[2] * 0.0722;
  return `#${rgb
    .map((channel) =>
      Math.round(grey + (channel - grey) * saturation)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}

function bounded(value: number | undefined, fallback: number, maximum: number) {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(0, Math.min(maximum, value))
    : fallback;
}

/**
 * Derive a display-only map for one valid route. The caller passes null when
 * routing fails. Original heights, footprints, labels and API data stay intact.
 * Only the latest projection is cached per source, so itineraries cannot retain
 * an unbounded history of geometry or graphics resources.
 */
export function applyRouteAppearance(
  document: MapDocument,
  route: MapRoute | null | undefined,
  style: MapRouteStyle | undefined,
): MapDocument {
  const source = mapAppearanceSourceDocument(document);
  const saturation = bounded(style?.contextSaturation, 1, 1);
  const lift = bounded(style?.endpointLift, 0, 100);
  if (
    !route ||
    route.from === route.to ||
    (saturation === 1 && lift === 0) ||
    !source.locations.some((location) => location.id === route.from) ||
    !source.locations.some((location) => location.id === route.to) ||
    isMapLocationHidden(source, route.from) ||
    isMapLocationHidden(source, route.to)
  )
    return source;

  const key = JSON.stringify([route.from, route.to, saturation, lift]);
  const cached = lastAppearances.get(source);
  if (cached?.key === key) return cached.document;
  let changed = false;
  const stands = source.geometry.stands.map((stand) => {
    if (stand.hidden) return stand;
    const endpoint = stand.id === route.from || stand.id === route.to;
    if (endpoint ? lift === 0 : saturation === 1) return stand;
    changed = true;
    const derived = endpoint
      ? { ...stand, height: mapStandHeight(stand) + lift }
      : { ...stand };
    inheritMapPreviewOpacity(stand, derived);
    if (!endpoint) {
      standSaturations.set(derived, saturation);
      const colors = mapStandColors(stand);
      const roof = desaturateMapColor(colors.roof, saturation);
      standColors.set(derived, {
        roof,
        ink: validSourceColor(stand) ? sourceInk(roof) : colors.ink,
      });
    }
    return derived;
  });
  if (!changed) return source;
  const derived = {
    ...source,
    geometry: { ...source.geometry, stands },
  };
  appearanceSources.set(derived, source);
  lastAppearances.set(source, { key, document: derived });
  return derived;
}
